import type { RefObject, MouseEvent } from 'react'
import { useMemo } from 'react'
import { DraggableElement } from './DraggableElement'
import type { LayoutConfig } from '@/utils/dealCalculations'
import type { SnapGuide } from './layoutSnap'
import type { SlideCategoryBlock } from './computeSlideCategoryBlocks'
import { SlidePreviewProjectType } from './SlidePreviewProjectType'
import { SlideScopeBlocks } from './SlideScopeBlocks'
import { lockedRocketGalaxyLayout } from '@/utils/planLayoutDefaults'
import {
  formatSlideDescriptionBody,
  SLIDE_DESCRIPTION_TEXT_STYLE,
  SLIDE_LAYOUT_FALLBACK,
  SLIDE_PRICE_COMPARE_STYLE,
  SLIDE_PRICE_MAIN_STYLE,
  SLIDE_SUPPORT_HOURS_TEXT_STYLE,
  SLIDE_SUPPORT_TIER_LINE_STYLE,
} from './slidePreviewTheme'
type SlidePreviewInvestmentViewProps = {
  previewRef: RefObject<HTMLDivElement>
  layout: LayoutConfig
  leftBlockWidth: number
  hidePricing?: boolean
  priceLabel: string
  priceCompareLabel?: string
  displayPlanName: string
  displayPlanDescription: string
  displaySupportHoursLabel?: string
  displaySupportTierLines?: string[]
  displayBonus: string
  displayProjectType: string
  displayPayment: string
  displayLoyalty: string
  displayNegotiationDeadline?: string
  planTipo: string
  projectType?: string
  draggingKey: string | null
  snapGuides: SnapGuide[]
  categoryBlocks: SlideCategoryBlock[]
  categoryBlockNumbers: Map<string, number>
  onDragStart: (e: MouseEvent, key: string) => void
  onMouseMove: (e: MouseEvent<HTMLDivElement>) => void
  onMouseUpOrLeave: () => void
}

export function SlidePreviewInvestmentView({
  previewRef,
  layout,
  leftBlockWidth,
  hidePricing = false,
  priceLabel,
  priceCompareLabel,
  displayPlanName,
  displayPlanDescription,
  displaySupportHoursLabel = '',
  displaySupportTierLines = [],
  displayBonus,
  displayProjectType,
  displayPayment,
  displayLoyalty,
  displayNegotiationDeadline = '',
  planTipo,
  projectType,
  draggingKey,
  snapGuides,
  categoryBlocks,
  categoryBlockNumbers,
  onDragStart,
  onMouseMove,
  onMouseUpOrLeave,
}: SlidePreviewInvestmentViewProps) {
  const totalScopeItems = useMemo(
    () => categoryBlocks.reduce((sum, block) => sum + block.items.length, 0),
    [categoryBlocks],
  )
  const compactScope = totalScopeItems > 18
  const categoryTitleSize = compactScope
    ? 'clamp(5px, 1.05cqw, 9px)'
    : 'clamp(6px, 1.4cqw, 13px)'
  const itemFontSize = compactScope
    ? 'clamp(4px, 0.9cqw, 8px)'
    : 'clamp(5px, 1.2cqw, 11px)'
  const checkFontSize = compactScope
    ? 'clamp(4px, 0.85cqw, 8px)'
    : 'clamp(5px, 1.1cqw, 11px)'

  const multiSupportTiers = displaySupportTierLines.length >= 2

  return (
    <div className="w-full flex flex-col items-center justify-center p-1 bg-[#05050d] border border-white/10 rounded-lg overflow-y-auto shrink-0 select-none">
      <div
        ref={previewRef}
        data-proposal-slide-preview=""
        className="relative w-full rounded-lg overflow-hidden shadow-2xl"
        style={{
          aspectRatio: '16/9',
          containerType: 'inline-size',
          backgroundColor: '#070712',
          backgroundImage:
            'radial-gradient(circle at 82% 18%, rgba(255,47,134,0.38), transparent 30%), radial-gradient(circle at 44% 54%, rgba(126,34,206,0.26), transparent 33%), linear-gradient(135deg, rgba(255,255,255,0.05), transparent 34%)',
        }}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUpOrLeave}
        onMouseLeave={onMouseUpOrLeave}
      >
        {snapGuides.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-[25]">
            {snapGuides.map((guide, i) =>
              guide.orientation === 'vertical' ? (
                <div
                  key={`v-${guide.position}-${i}`}
                  className="absolute top-0 bottom-0 w-px bg-[#40C351] opacity-90 shadow-[0_0_6px_rgba(64,195,81,0.8)]"
                  style={{ left: `${guide.position}%` }}
                />
              ) : (
                <div
                  key={`h-${guide.position}-${i}`}
                  className="absolute left-0 right-0 h-px bg-[#40C351] opacity-90 shadow-[0_0_6px_rgba(64,195,81,0.8)]"
                  style={{ top: `${guide.position}%` }}
                />
              ),
            )}
          </div>
        )}

        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-[3.8%] top-[7%] h-[5%] w-[2px] rounded-full bg-[#ff2f86] shadow-[0_0_18px_rgba(255,47,134,0.95)]" />
          <div className="absolute right-[3%] top-[1.2%] rounded-full border border-[#ff2f86]/40 px-2 py-0.5 text-[clamp(5px,1cqw,10px)] font-bold uppercase tracking-[0.22em] text-[#ff2f86]">
            Rocket Search
          </div>
          <div className="absolute bottom-[0.8%] left-[4%] h-[1px] w-[32%] bg-gradient-to-r from-[#ff2f86] to-transparent" />
        </div>

        <DraggableElement
          x={leftBlockWidth * 0.06}
          y={layout.titleY || SLIDE_LAYOUT_FALLBACK.titleY}
          width={`${leftBlockWidth * 0.88}%`}
          dragging={draggingKey === 'title'}
          onMouseDown={(e) => onDragStart(e, 'title')}
          blockNumber={2}
        >
          <span style={{ color: '#fff', fontSize: 'clamp(10px, 3.6cqw, 32px)', fontWeight: 800, fontFamily: 'Arial, sans-serif' }}>
            Plano Rocket Search
          </span>
          <span style={{ color: '#ff2f86', fontSize: 'clamp(10px, 3.6cqw, 32px)', fontWeight: 700 }}>.</span>
        </DraggableElement>

        <SlidePreviewProjectType
          planName={displayPlanName}
          planTipo={planTipo}
          projectType={projectType}
          displayProjectType={displayProjectType}
          leftBlockWidth={leftBlockWidth}
          projectTypeY={layout.projectTypeY}
          dragging={draggingKey === 'projectType'}
          onDragStart={onDragStart}
        />

        {displayPlanDescription && (
          <DraggableElement
            x={leftBlockWidth * 0.06}
            y={layout.descriptionY || SLIDE_LAYOUT_FALLBACK.descriptionY}
            width={`${leftBlockWidth * 0.88}%`}
            dragging={draggingKey === 'description'}
            onMouseDown={(e) => onDragStart(e, 'description')}
            blockNumber={4}
          >
            <p style={SLIDE_DESCRIPTION_TEXT_STYLE}>
              {formatSlideDescriptionBody(displayPlanDescription)}
            </p>
          </DraggableElement>
        )}

        {displaySupportHoursLabel && !multiSupportTiers && (
          <DraggableElement
            x={leftBlockWidth * 0.06}
            y={layout.supportHoursY ?? SLIDE_LAYOUT_FALLBACK.supportHoursY}
            width={`${leftBlockWidth * 0.88}%`}
            dragging={draggingKey === 'supportHours'}
            onMouseDown={(e) => onDragStart(e, 'supportHours')}
            blockNumber={5}
          >
            <p style={SLIDE_SUPPORT_HOURS_TEXT_STYLE}>{displaySupportHoursLabel}</p>
          </DraggableElement>
        )}

        {multiSupportTiers && (
          <DraggableElement
            x={leftBlockWidth * 0.06}
            y={layout.supportHoursY ?? SLIDE_LAYOUT_FALLBACK.supportHoursY}
            width={`${leftBlockWidth * 0.88}%`}
            dragging={draggingKey === 'supportHours'}
            onMouseDown={(e) => onDragStart(e, 'supportHours')}
            blockNumber={5}
          >
            <div className="space-y-1">
              {displaySupportTierLines.map((line) => (
                <p key={line} style={SLIDE_SUPPORT_TIER_LINE_STYLE}>
                  • {line}
                </p>
              ))}
            </div>
          </DraggableElement>
        )}

        {!hidePricing && displayBonus && (
          <DraggableElement
            x={leftBlockWidth * 0.06}
            y={layout.bonusY || SLIDE_LAYOUT_FALLBACK.bonusY}
            width={`${leftBlockWidth * 0.88}%`}
            dragging={draggingKey === 'bonus'}
            onMouseDown={(e) => onDragStart(e, 'bonus')}
            blockNumber={6}
          >
            <div
              className="py-1 px-1.5 rounded border border-[#ff2f86]/35 bg-[#ff2f86]/10 text-[#ff2f86] font-semibold flex items-center gap-1 shadow-[0_0_12px_rgba(255,47,134,0.22)]"
              style={{ fontSize: 'clamp(4px, 1.2cqw, 11px)' }}
            >
              <span>★</span> BÔNUS: {displayBonus}
            </div>
          </DraggableElement>
        )}

        {!hidePricing && !multiSupportTiers && (
          <DraggableElement
            x={leftBlockWidth * 0.06}
            y={layout.priceY || SLIDE_LAYOUT_FALLBACK.priceY}
            width={`${leftBlockWidth * 0.88}%`}
            dragging={draggingKey === 'price'}
            onMouseDown={(e) => onDragStart(e, 'price')}
            blockNumber={7}
          >
            <div className="flex flex-col gap-0.5 rounded-xl border border-[#ff2f86]/25 bg-white/[0.045] px-3 py-2 shadow-[0_0_28px_rgba(255,47,134,0.14)]">
              <span className="text-[clamp(4px,1cqw,9px)] font-bold uppercase tracking-[0.18em] text-[#ff2f86]">
                Investimento mensal
              </span>
              {priceCompareLabel && (
                <span style={SLIDE_PRICE_COMPARE_STYLE}>De {priceCompareLabel}</span>
              )}
              <span style={SLIDE_PRICE_MAIN_STYLE}>
                {priceCompareLabel ? `Por ${priceLabel}` : priceLabel}
              </span>
            </div>
          </DraggableElement>
        )}

        {!hidePricing && displayPayment && (
          <DraggableElement
            x={leftBlockWidth * 0.06}
            y={layout.paymentY || SLIDE_LAYOUT_FALLBACK.paymentY}
            width={`${leftBlockWidth * 0.88}%`}
            dragging={draggingKey === 'payment'}
            onMouseDown={(e) => onDragStart(e, 'payment')}
            blockNumber={8}
          >
          <span style={{ color: '#c8cade', fontSize: 'clamp(5px, 1.2cqw, 11px)', fontFamily: 'Arial, sans-serif' }}>
              {displayPayment}
            </span>
          </DraggableElement>
        )}

        {!hidePricing && displayLoyalty && (
          <DraggableElement
            x={leftBlockWidth * 0.06}
            y={lockedRocketGalaxyLayout(displayPlanName)?.loyaltyY ?? (layout.loyaltyY || SLIDE_LAYOUT_FALLBACK.loyaltyY)}
            width={`${leftBlockWidth * 0.88}%`}
            dragging={draggingKey === 'loyalty'}
            onMouseDown={(e) => onDragStart(e, 'loyalty')}
            blockNumber={9}
          >
            <span style={{ color: '#a8abc2', display: 'block', fontSize: 'clamp(5px, 1.2cqw, 11px)', lineHeight: 1.15, fontFamily: 'Arial, sans-serif' }}>
              {displayLoyalty}
            </span>
          </DraggableElement>
        )}

        {!hidePricing && displayNegotiationDeadline && (
          <DraggableElement
            x={leftBlockWidth * 0.06}
            y={layout.negotiationY ?? SLIDE_LAYOUT_FALLBACK.negotiationY}
            width={`${leftBlockWidth * 0.88}%`}
            dragging={draggingKey === 'negotiation'}
            onMouseDown={(e) => onDragStart(e, 'negotiation')}
            blockNumber={10}
          >
            <span style={{ color: '#8e92ad', fontSize: 'clamp(4px, 1.05cqw, 10px)', fontFamily: 'Arial, sans-serif' }}>
              {displayNegotiationDeadline}
            </span>
          </DraggableElement>
        )}

        <div
          className="absolute top-0 bottom-0 cursor-col-resize group z-20 flex items-center justify-center"
          style={{ left: `${leftBlockWidth}%`, width: '10px', transform: 'translateX(-5px)' }}
          onMouseDown={(e) => onDragStart(e, 'leftBlockWidth')}
        >
          <span
            className="absolute top-2 left-1/2 -translate-x-1/2 z-30 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#ff2f86] text-[9px] font-black text-white opacity-0 shadow-[0_0_12px_rgba(255,47,134,0.45)] transition-opacity group-hover:opacity-100 pointer-events-none"
            title="Bloco 1 — Divisor bloco verde"
          >
            1
          </span>
          <div className="h-full w-px bg-[#ff2f86]/0 transition-all group-hover:w-[2px] group-hover:bg-[#ff2f86]/60 group-active:bg-[#ff2f86]" />
        </div>

        <DraggableElement
          x={leftBlockWidth + 4}
          y={layout.planTitleY || 2.7}
          width={`${100 - leftBlockWidth - 8}%`}
          dragging={draggingKey === 'planTitle'}
          onMouseDown={(e) => onDragStart(e, 'planTitle')}
          blockNumber={11}
        >
          <span
            style={{
              color: '#ffffff',
              display: 'block',
              fontSize: 'clamp(8px, 2cqw, 18px)',
              fontWeight: 800,
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.05,
              whiteSpace: 'nowrap',
            }}
          >
            {displayPlanName || 'Nome do Plano'}
          </span>
        </DraggableElement>

        <SlideScopeBlocks
          blocks={categoryBlocks}
          compact={compactScope}
          categoryTitleSize={categoryTitleSize}
          itemFontSize={itemFontSize}
          checkFontSize={checkFontSize}
          draggingKey={draggingKey}
          blockNumbers={categoryBlockNumbers}
          onDragStart={onDragStart}
        />
      </div>

      <p className="text-[10px] text-text-muted mt-2 text-center">
        Arraste os elementos no preview para ajustar posições · Arraste a barra para redimensionar as colunas
      </p>
    </div>
  )
}
